/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { AppState } from "Dataflows/Reducers";
import { IApplicationGetAssign } from "Interfaces/Application/IApplicationGetAssign";
import { IApplicationListRowData } from "Interfaces/Application/IApplicationListRowData";
import { IFilterValue } from "Interfaces/IFilterValue";

import { IApplicationState } from "./IApplicationState";
import { IApplicationGetBulkAssign } from "Interfaces/Application/IApplicationGetBulkAssign";

/**
 * Returns the application data.
 * @param applicationState
 */
export const getApplications = (applicationState: IApplicationState): IApplicationListRowData[] =>
  applicationState.applications;

/**
 * Returns current value from state for: applicationsFilterValues
 * @param applicationState
 */
export const getApplicationsFilterValues = (applicationState: IApplicationState): IFilterValue[] =>
  applicationState.applicationsFilterValues;

/**
 * Returns current value from state for: currentPage
 * @param applicationState
 */
export const getCurrentPage = (applicationState: IApplicationState): number =>
  applicationState.currentPage;

/**
 * Returns the filtered count.
 * @param applicationState
 */
export const getFilteredCount = (applicationState: IApplicationState): number =>
  applicationState.filteredCount;

/**
 * Returns the application load error.
 * @param applicationState
 */
export const getLoadApplicationsError = (applicationState: IApplicationState) =>
  applicationState.loadApplicationsError;

/**
 * Returns current value from state for: pageSize
 * @param applicationState
 */
export const getPageSize = (applicationState: IApplicationState): number =>
  applicationState.pageSize;

/**
 * Returns current value from state for: searchText
 * @param applicationState
 */
export const getSearchText = (applicationState: IApplicationState): string =>
  applicationState.searchText;

/**
 * Returns current value from state for: selectedApplication
 * @param applicationState
 */
export const getSelectedApplication = (applicationState: IApplicationState) =>
  applicationState.selectedApplication;

/**
 * Returns current value from state for: selectedApplicationEmail
 * @param applicationState
 */
export const getSelectedApplicationEmail = (applicationState: IApplicationState) =>
  applicationState.selectedApplicationEmail;

/**
 * Returns current value from state for: loadSelectedApplicationError
 * @param applicationState
 */
export const getLoadSelectedApplicationError = (applicationState: IApplicationState) =>
  applicationState.loadSelectedApplicationError;

/**
 * Returns the boolean value for whether or not
 * application data is still being loaded.
 * @param applicationState
 */
export const isLoadingApplications = (applicationState: IApplicationState) =>
  applicationState.isLoadingApplications;

/**
 * Returns the boolean value for whether or not
 * the selected application data is still being loaded.
 * @param {AppState} state The application state.
 * @returns {boolean} True if the selected application data is still being loaded.
 */
export const isLoadingSelectedApplication = (applicationState: IApplicationState): boolean =>
  applicationState.isLoadingSelectedApplication;

/**
 * Returns file down load data.
 * @param {AppState} state
 */
export const getFileDownloadData = (applicationState: IApplicationState): string | undefined =>
  applicationState.fileDownloadData;

/**
 * Returns file down load status.
 * @param {AppState} state
 */
export const getFileDownloadStatus = (applicationState: IApplicationState): OperationStatus =>
  applicationState.fileDownloadStatus;

export const getCurrentTenant = (applicationState: IApplicationState): string =>
  applicationState.tenant;

export const getAssignApplicationInfoStatus = (state: AppState): OperationStatus =>
  state.applicationState.assignApplicationInfoStatus;

export const getAssignApplicationInfoErrorMessage = (state: AppState): string =>
  state.applicationState.assignApplicationInfoErrorMessage;

export const getAssignApplicationInfo = (state: AppState): IApplicationGetAssign | undefined =>
  state.applicationState.assignApplicationInfo;

export const getAvailableAssignApplicationInfoStatus = (state: AppState): OperationStatus =>
  state.applicationState.assignAvailableApplicationInfoStatus;

export const getAvailableAssignApplicationInfoErrorMessage = (state: AppState): string =>
  state.applicationState.assignAvailableApplicationInfoErrorMessage;

export const getAvailableAssignApplicationInfo = (
  state: AppState
): IApplicationGetBulkAssign | undefined => state.applicationState.assignAvailableApplicationInfo;

export const getSortColumn = (state: AppState): string | undefined =>
  state.applicationState.sortColumn;

export const getSortDescending = (state: AppState): boolean | undefined =>
  state.applicationState.sortDescending;

/**
 * Gets the status for AssignApplication.
 * @param {AppState} state
 */
export const getAssignApplicationStatus = (state: AppState): OperationStatus =>
  state.applicationState.assignApplicationStatus;

/**
 * Gets the error message for AssignApplication.
 * @param {AppState} state
 */
export const getAssignApplicationErrorMessage = (state: AppState): string =>
  state.applicationState.assignApplicationErrorMessage;

/**
 * Gets the status for AssignApplication.
 * @param {AppState} state
 */
export const getBulkAssignApplicationStatus = (state: AppState): OperationStatus =>
  state.applicationState.assignBulkApplicationStatus;

/**
 * Gets the error message for AssignApplication.
 * @param {AppState} state
 */
export const getBulkAssignApplicationErrorMessage = (state: AppState): string =>
  state.applicationState.assignBulkApplicationErrorMessage;
/**
 * Gets the status for ScoreApplication.
 * @param {AppState} state
 */
export const getScoreApplicationStatus = (state: AppState): OperationStatus =>
  state.applicationState.scoreApplicationStatus;

/**
 * Gets the error message for ScoreApplication.
 * @param {AppState} state
 */
export const getScoreApplicationErrorMessage = (state: AppState): string =>
  state.applicationState.scoreApplicationErrorMessage;

/**
 * Gets the status for flaggin an application.
 * @param {AppState} state
 */
export const getFlagApplicationStatus = (state: AppState): OperationStatus =>
  state.applicationState.flagApplicationStatus;

/**
 * Gets the error message for flagging an application.
 * @param {AppState} state
 */
export const getFlagApplicationErrorMessage = (state: AppState): string =>
  state.applicationState.flagApplicationErrorMessage;
